// services/abandonedCartService/save.ts - services/abandonedCartService.ts split (sub-module save)


import { ABANDONED_CART_ERROR_EVENT } from './events.js';
import { API_BASE } from './config.js';
import { API_KEY } from './config.js';
import { FALLBACK_ENABLED } from './config.js';
import type { SaveCartParams } from './types.js';
import { saveViaSupabaseRPC } from './rpcFallback.js';



export async function saveAbandonedCart(params: SaveCartParams): Promise<{ id: string; action: string } | null> {
  const startTime = Date.now();

  const fail = (status: number, error: unknown) => {
    // Toast HANYA bila fallback juga gagal/nonaktif — error API mentah (mis.
    // halaman HTML Cloudflare 502) jangan pernah jadi teks toast.
    window.dispatchEvent(new CustomEvent(ABANDONED_CART_ERROR_EVENT, {
      detail: { status, error, source_type: params.source_type, elapsed: Date.now() - startTime }
    }));
  };

  const tryFallback = async (): Promise<{ id: string; action: string } | null> => {
    if (!FALLBACK_ENABLED) return null;
    try {
      const r = await saveViaSupabaseRPC(params);
      if (r?.id) return r;
    } catch { /* fallback ikut gagal → fail() di bawah */ }
    return null;
  };

  try {
    // Guard kontak: tanpa nomor valid (digit >= 8) dan tanpa email, cart tidak
    // bisa di-follow-up — jangan kirim (dulu jadi baris sampah "62"/""/"Unknown").
    // Skip diam-diam: bukan error, jangan fallback RPC.
    const digits = String(params.customerPhone || '').replace(/\D/g, '');
    const hasPhone = digits.length >= 8;
    const hasEmail = String(params.customerEmail || '').includes('@');
    if (!hasPhone && !hasEmail) return null;
    // Pola test/QA/dummy: jangan masuk antrean kirim (insiden 2026-10-07:
    // cart "TEST QA -" 6281234567891 → 33 SKIP retry). Skip diam-diam.
    const nm = String((params as { customerName?: string }).customerName || '');
    if (/test|qa|dummy|coba|trial|asdf|xxx/i.test(nm)) return null;
    if (/1234567891$|2345678901$|3456789012$/.test(digits)) return null;
    if (/^(\d)\1{6,}$/.test(digits.replace(/^62/, ''))) return null;

    const { cartId, ...cartData } = params;
    const body = { cartId, ...cartData };

    const res = await fetch(`${API_BASE}/api/abandoned-carts/upsert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(10000),
    });

    if (!res.ok) {
      const errorText = await res.text().catch(() => '');
      console.warn('[AbandonedCart] API error:', res.status, String(errorText).slice(0, 200));
      const fb = await tryFallback();
      if (fb) return fb;
      fail(res.status, errorText);
      return null;
    }

    const json = await res.json();
    // Backend short-circuit 'recovered' (sudah ada order aktif): bukan cart,
    // tidak ada id. Teruskan apa adanya — JANGAN fallback RPC (akan insert lagi).
    if (json.action === 'recovered') {
      return { id: '', action: 'recovered' };
    }
    if (json.success && json.data?.id) {
      return { id: json.data.id, action: json.action };
    }
    console.warn('[AbandonedCart] Response tanpa data.id: status', res.status, 'source_type:', params.source_type);
    {
      const fb = await tryFallback();
      if (fb) return fb;
      fail(200, 'Response tanpa data.id');
    }
    return null;
  } catch (err) {
    console.warn('[AbandonedCart] Save error:', err);
    {
      const fb = await tryFallback();
      if (fb) return fb;
      fail(0, err instanceof Error ? err.message : String(err));
    }
    return null;
  }
}
